import { useCountry } from '@/lib/country'
import { cx } from './ui/primitives'

/**
 * Which country everything on screen belongs to.
 *
 * Permanent chrome rather than a filter inside each screen, because country scopes
 * pricing, letterhead, terms, numbering and the approval chain. A rep who does not
 * know which country they are in is one click from quoting a Singapore customer on
 * a Malaysian letterhead.
 *
 * It sits in the header rather than in a strip of its own. The strip cost every screen
 * a full row to carry one control, and a quotation table is a tall thing before it is
 * anything else.
 */
export function CountrySwitcher() {
  const { current, available, select } = useCountry()

  if (available.length === 0) return null

  const dot = <span className="brand-gradient size-2 shrink-0 rounded-full" aria-hidden />

  if (available.length === 1) {
    // One grant is not a choice. Showing a dropdown with a single option invites
    // people to look for the others.
    return (
      <div className="flex items-center gap-2 rounded-md border border-transparent px-2 py-1">
        {dot}
        <span className="text-[13px] font-medium">
          {current?.name} <span className="text-muted">· {current?.currencyCode}</span>
          {current && !current.isActive && <span className="text-warn"> · not yet active</span>}
        </span>
      </div>
    )
  }

  return (
    <div className="relative flex items-center">
      {/* The dot and the chevron are painted under a transparent select, so the control
          keeps the platform's own dropdown - and its keyboard and touch behaviour -
          without inheriting the platform's idea of what a select looks like. */}
      <span className="pointer-events-none absolute left-2.5 flex items-center">{dot}</span>

      <label htmlFor="country-context" className="sr-only">
        Country
      </label>

      <select
        id="country-context"
        value={current?.countryId ?? ''}
        onChange={(event) => select(event.target.value)}
        className={cx(
          'h-8 cursor-pointer appearance-none rounded-md border border-line bg-surface',
          'py-0 pl-7 pr-7 text-[13px] font-medium text-ink',
          'hover:bg-surface-2 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30',
        )}
      >
        {/* A country being set up is granted before it is activated, so it belongs in this
            list - the terms document that activates it is uploaded through this very
            control. Saying which ones cannot trade yet is what stops the empty screens
            behind them looking broken. */}
        {available.map((country) => (
          <option key={country.countryId} value={country.countryId}>
            {country.name} · {country.currencyCode}
            {country.isActive ? '' : ' · not yet active'}
          </option>
        ))}
      </select>

      <svg
        aria-hidden
        viewBox="0 0 12 12"
        className="pointer-events-none absolute right-2 size-3 text-muted"
      >
        <path
          d="M2.5 4.5 6 8l3.5-3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  )
}

/**
 * Somebody with no country at all cannot use any screen in the application, so this is a
 * banner across the page rather than a hint on a control. It stays out of the header:
 * the header is for controls, and this is not one.
 */
export function CountryNotice() {
  const { available, current } = useCountry()

  if (available.length === 0) {
    return (
      <div className="border-b border-line bg-warn-wash px-5 py-2 text-[13px] text-warn">
        You have no country access yet. An Owner or Manager has to grant it before you can
        see customers, products or quotations.
      </div>
    )
  }

  // A country being set up is reachable before it can trade, so every listing behind this
  // is legitimately empty. Said once, at the top, rather than left to each screen to
  // explain - or worse, to look broken.
  if (current && !current.isActive) {
    return (
      <div className="border-b border-line bg-warn-wash px-5 py-2 text-[13px] text-warn">
        {current.name} is not active yet, so nothing can be raised in it. Publish its terms
        document under Terms, then activate it under Countries.
      </div>
    )
  }

  return null
}
